import { useRect } from '@reach/rect';
import { useField } from 'formik';
import * as React from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';

import { HelperText } from '@/components/HelperText';
import { useControlled } from '@/hooks/useControlled';
import { useForkRef } from '@/hooks/useForkRef';
import type { FormikFieldValiadtionType } from '@/types';
import { getAriaProps } from '@/utils/getAriaProps';

import type { CommonTypes } from './common';
import {
  BaseInputStyle,
  LabelHiddenStyle,
  LabelStyle,
  NoErrorInputStyle,
} from './common';

export type InputType = CommonTypes &
  Omit<React.InputHTMLAttributes<HTMLInputElement>, 'css'> & {
    endAndornment?: React.ReactNode;
    error?: boolean;
    helperText?: string;
    startAndornment?: React.ReactNode;
    textCenter?: boolean;
  };

type FormikInputType = Omit<InputType, 'name'> &
  Required<Pick<InputType, 'name'>>;

type StyledInputType = Pick<InputType, 'error'> & { textCenter: boolean };

const StyledContainer = styled.div`
  ${tw`relative rounded-lg`}
`;

const StyledInput = styled.input<StyledInputType>(({ error, textCenter }) => [
  BaseInputStyle,
  error &&
    tw`border-red-600 bg-red-100 focus:ring-red-600 focus:border-red-600`,
  !error && NoErrorInputStyle,
  textCenter && tw`text-center`,
]);

const StyledLabel = styled.label<Pick<InputType, 'error' | 'labelHidden'>>`
  ${({ labelHidden }) => (labelHidden ? LabelHiddenStyle : LabelStyle)}
  ${({ error }) => (error ? tw`text-red-600` : tw`text-gray-900`)}
`;

export const Input = React.forwardRef<HTMLInputElement, InputType>(
  (
    {
      autoComplete = 'off',
      defaultValue = '',
      endAndornment,
      error = false,
      helperText,
      label,
      labelHidden,
      name = '',
      readOnly,
      required,
      value,
      startAndornment,
      textCenter = false,
      type = 'text',
      onChange,
      ...rest
    },
    ref
  ) => {
    const internalInputRef = React.useRef<HTMLInputElement>(null);
    const startAndornmentRef = React.useRef<HTMLDivElement>(null);
    const endAndornmentRef = React.useRef<HTMLDivElement>(null);
    const [inputValue, setValue, isControlled] = useControlled({
      controlled: value,
      default: String(defaultValue),
      name: 'Input',
    });
    const changeHandler = isControlled
      ? onChange
      : (event: React.ChangeEvent<HTMLInputElement>) =>
          setValue(event.target.value);

    const inputRef = useForkRef(ref, internalInputRef);

    useRect(startAndornmentRef, {
      onChange: (rect) => {
        if (internalInputRef.current)
          internalInputRef.current.style.paddingLeft = `${rect.width + 4}px`;
      },
    });
    useRect(endAndornmentRef, {
      onChange: (rect) => {
        if (internalInputRef.current)
          internalInputRef.current.style.paddingRight = `${rect.width + 4}px`;
      },
    });

    return (
      <div>
        {label ? (
          <StyledLabel
            htmlFor={name}
            error={error}
            labelHidden={labelHidden}
            data-needl-input-label
          >
            {label}
            {required ? <span className='text-red-600'>&nbsp;*</span> : null}
          </StyledLabel>
        ) : null}
        <StyledContainer>
          {React.isValidElement(startAndornment)
            ? React.cloneElement(startAndornment, {
                ref: startAndornmentRef,
                position: 'start',
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
              } as any)
            : startAndornment}
          <StyledInput
            autoComplete={autoComplete}
            ref={inputRef}
            name={name}
            id={name}
            value={inputValue}
            onChange={changeHandler}
            type={type}
            error={error}
            readOnly={readOnly}
            required={required}
            textCenter={textCenter}
            data-needl-input
            {...rest}
            {...getAriaProps({ error, name })}
          />
          {React.isValidElement(endAndornment)
            ? React.cloneElement(endAndornment, {
                ref: endAndornmentRef,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
              } as any)
            : endAndornment}
        </StyledContainer>
        {helperText ? (
          <HelperText error={error} {...(error && { id: `${name}-error` })}>
            {helperText}
          </HelperText>
        ) : null}
      </div>
    );
  }
);

export const FormikInputField = React.forwardRef<
  HTMLInputElement,
  FormikFieldValiadtionType & FormikInputType
>(({ name, helperText, showHelperText = true, validate, ...rest }, ref) => {
  const [field, meta] = useField({ name, validate });
  const errorText =
    meta.error && (process.env.NODE_ENV === 'test' ? true : meta.touched)
      ? meta.error
      : '';

  return (
    <Input
      error={!!errorText}
      helperText={showHelperText ? errorText || helperText : ''}
      {...rest}
      {...field}
      name={name}
      ref={ref}
    />
  );
});
